import React, { useEffect, useMemo, useRef, useState } from "react";

export type InlineSelectOption = { value: string; label: string };

type InlineSelectSize = "sm" | "md";

type InlineSelectProps = Readonly<{
  value: string;
  options: readonly InlineSelectOption[];
  onChange: (next: string) => void;
  disabled?: boolean;
  size?: InlineSelectSize;
  maxListHeight?: number;
  zIndex?: number;
  /** Shown (muted) when value is empty and matches no option */
  placeholder?: string;
  ariaLabel?: string;
}>;

export function InlineSelect({
  value,
  options,
  onChange,
  disabled,
  size = "md",
  maxListHeight = 220,
  zIndex = 2000,
  placeholder,
  ariaLabel,
}: InlineSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const selectedLabel = useMemo(() => {
    return options.find((o) => o.value === value)?.label ?? value;
  }, [options, value]);
  const showPlaceholder = !selectedLabel && !!placeholder;

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (containerRef.current && containerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.focus();
  }, [open]);

  const paddingY = size === "sm" ? 6 : 10;
  const fontSize = 14;
  const chevronFontSize = 12;

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          textAlign: "left",
          padding: `${paddingY}px 36px ${paddingY}px 12px`,
          fontSize,
          borderRadius: 6,
          border: "1px solid var(--border)",
          background: "white",
          color: "var(--text-dark)",
          ...(disabled && { opacity: 0.7, cursor: "not-allowed" }),
        }}
      >
        {showPlaceholder ? <span style={{ color: "var(--text-muted)" }}>{placeholder}</span> : selectedLabel}
        <span
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: open ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
            pointerEvents: "none",
            fontSize: chevronFontSize,
            color: "var(--text-muted)",
            transition: "transform 0.15s ease",
          }}
          aria-hidden
        >
          ▼
        </span>
      </button>

      {open && !disabled && (
        <ul
          ref={listRef}
          tabIndex={-1}
          role="listbox"
          aria-activedescendant={value}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 6px)",
            margin: 0,
            padding: 6,
            listStyle: "none",
            background: "white",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            maxHeight: maxListHeight,
            overflowY: "auto",
            zIndex,
            outline: "none",
          }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            const handleSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(opt.value);
              setTimeout(() => {
                setOpen(false);
                buttonRef.current?.focus();
              }, 0);
            };
            const handleKeyDown = (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") handleSelect(e);
            };
            return (
              <li
                key={opt.value || "__empty__"}
                id={opt.value}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSelect}
                onKeyDown={handleKeyDown}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: isSelected ? "rgba(0, 123, 255, 0.10)" : "transparent",
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: 13,
                }}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

